/**
 * Who is signed in, and what the business is configured as — resolved once,
 * at the top of the app, and shared by every screen.
 *
 * There used to be two identity models. `/api/auth/me` gave the session, and
 * then the radar separately fetched `/api/listings/clients` and took `rows[0]`
 * as "the business" — a leftover from when the product ran a Baileys socket
 * per broker. That second fetch had no cache, ran on every visit to the radar,
 * and gated the whole screen behind it, so entering the radar meant watching
 * "the system is not configured yet" flash while a second identity loaded.
 *
 * CLAUDE.md says it plainly: one user, one business. So both reads happen here,
 * in parallel, once — and `business` is a field of the session rather than a
 * thing each screen goes and finds for itself.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ServiceId, Scope } from "@bootwhat/shared/access";

export interface SessionProfile {
  tenantId: string;
  userId: string;
  isAdmin: boolean;
  allowedServices: ServiceId[];
  scopes: Scope[];
}

/** The single configuration document behind the capture engine. */
export interface Business {
  _id: string;
  name: string;
  phone: string;
  active: boolean;
  waStatus: "connected" | "connecting" | "waiting_qr" | "disconnected" | string;
  watchedGroups?: string[];
  minConfidence?: number;
  minTextLength?: number;
  dedupEnabled?: boolean;
  dedupWindowHours?: number;
}

export interface Session {
  /** True until the first answer lands. Nothing is decided before then. */
  loading: boolean;
  profile: SessionProfile | null;
  business: Business | null;
  /** Is the capture socket paired right now? */
  connected: boolean;
  can: (scope: Scope) => boolean;
  /** Re-read everything — after sign-in, or a change to access. */
  refresh: () => Promise<void>;
  /** Re-read only the business — after saving settings or pairing. */
  refreshBusiness: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: "no-store", credentials: "include" });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    // Offline, or the server is restarting. Treat as "nothing known" rather
    // than leaving the app parked on a spinner forever.
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<SessionProfile | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);

  const refreshBusiness = useCallback(async () => {
    const rows = await getJson<Business[]>("/api/listings/clients");
    setBusiness(rows?.[0] ?? null);
  }, []);

  const refresh = useCallback(async () => {
    // In parallel: the business read is gated on `wre.manage` server-side and
    // simply answers 403 for a user without it, which getJson reads as null.
    const [next] = await Promise.all([
      getJson<SessionProfile>("/api/auth/me"),
      refreshBusiness(),
    ]);
    setProfile(next);
    setLoading(false);
  }, [refreshBusiness]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<Session>(() => ({
    loading,
    profile,
    business,
    connected: business?.waStatus === "connected",
    // Admins hold every scope — the server's getAccessProfile does the same.
    can: (scope) => Boolean(profile && (profile.isAdmin || profile.scopes.includes(scope))),
    refresh,
    refreshBusiness,
  }), [loading, profile, business, refresh, refreshBusiness]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
